/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {getFileHistory} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UsePendingFileHistoryResult = UseDefaultQueryResult & {
  pendingFileHistory: GenericObject[];
  refresh: () => void;
};

/**
 * Retrieves the device pending file history for the given device esn.
 *
 * ```typescript
 *
 * import {usePendingFileHistory} from '@ion/query/src/hooks/';
 *
 *  ...
 *
 *  const {pendingFileHistory} = usePendingFileHistory();
 *
 * ```
 *
 * import {usePendingFileHistory} from '@ion/query/src/hooks/';
 * @param esn of the device to retrieve file history for
 * @param staleTime how long the query should hold onto details before re-fetching
 * @returns usePendingFileHistory containing the query results and the file history.
 */
export const usePendingFileHistory = (
  esn: string,
  staleTime = getStaleTime(1, false, false)
): UsePendingFileHistoryResult => {
  const queryClient = useQueryClient();
  const key         = useMemo(() => (
    createKey(QueryKey.PENDING_FILE_HISTORY, esn)
  ), [esn]);
  const query       = useQuery(key, async() => {
    const results = await getFileHistory(esn);
    return results;
  }, {
    staleTime : staleTime as number,
    enabled   : !!esn
  });

  useEffect(() => {
    if (!query.data && esn && !query.isError) {
      query.refetch();
    }
  }, [query, esn]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(key);
  }, [key]);

  return createQueryResult(query, {
    pendingFileHistory    : query.data || {},
    pendingHistoryLoading : query.isLoading,
    refresh
  }) as UsePendingFileHistoryResult;
};

export default usePendingFileHistory;
